import { graphql } from "@/shared/gql";
import { useCompanyId } from "@/entities/company/hooks/use-company-id";
import { useSuspenseQuery } from "@apollo/client";
import {
  ContentType,
  FetchViewsAndColumnsQueryVariables,
} from "@/shared/gql/graphql";
import { EntityNotFoundError } from "@/shared/lib/errors/404-not-found-error";
import { getPreloadQuery } from "@/shared/gql/apollo-client";

export const FetchViewsAndColumns = graphql(`
  query FetchViewsAndColumns(
    $companyId: ID!
    $contentType: ContentType!
    $includeReserved: Boolean
  ) {
    currentUser(company: $companyId) {
      id
      views(
        input: { contentType: $contentType, includeReserved: $includeReserved }
      ) {
        id
        ...AllViewProps
      }
    }
    columns(contentType: $contentType) {
      id
      ...OwnColumnProps
    }
  }
`);

type Props = {
  contentType: ContentType;
  includeReserved?: boolean;
};

// The route loader preloads this query and the component then reads it back. Apollo keys
// cache entries on the exact variables, so an omitted `includeReserved` and an explicit
// `true` are two different entries: the preload would be discarded and the component would
// refetch, pushing the page's own data a full round trip later. Both paths build their
// variables here so they always match.
const viewsQueryVariables = ({
  companyId,
  contentType,
  includeReserved,
}: FetchViewsAndColumnsQueryVariables): FetchViewsAndColumnsQueryVariables => ({
  companyId,
  contentType,
  includeReserved: includeReserved ?? true,
});

export const useFetchViewsAndColumnsQuery = ({
  contentType,
  includeReserved,
}: Props) => {
  const companyId = useCompanyId();

  const result = useSuspenseQuery(FetchViewsAndColumns, {
    variables: viewsQueryVariables({ companyId, contentType, includeReserved }),
  });

  if (result.error) {
    throw result.error;
  }

  if (!result.data) {
    throw new EntityNotFoundError("Data");
  }

  if (!result.data.currentUser.views) {
    throw new EntityNotFoundError("Views");
  }

  if (!result.data.columns) {
    throw new EntityNotFoundError("Columns");
  }

  return {
    views: result.data.currentUser.views,
    columns: result.data.columns,
  };
};

export const preloadViewsQuery = (vars: FetchViewsAndColumnsQueryVariables) => {
  getPreloadQuery()(FetchViewsAndColumns, {
    variables: viewsQueryVariables(vars),
  });
};
